/**
 * Account-pair AI mode picker — labeled dropdown (Manual / Semi / Auto).
 *
 *   Manual    -> Haiku silent. Operator owns the pair entirely.
 *   Semi      -> Haiku reasons; entry/exit decisions queue for operator
 *                approval; safety reflexes (news_protect / restore /
 *                force_close) auto-execute.
 *   Auto      -> Haiku autonomous (current behaviour).
 *
 * Used to be a 3-state segmented toggle, but operators couldn't tell at
 * a glance which group did what. Now it's an explicit labeled dropdown
 * with a description on every option, and the trigger paints in the
 * pink accent so the active mode reads immediately.
 *
 * Disabled while the pair is in 'setup' or 'closed' — those need
 * Activate / re-open flows respectively, not a mode flip.
 */

import { useState, type ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { PairMode } from "@pfh/shared";

interface PairModeToggleProps {
  mode: PairMode;
  status: "setup" | "active" | "closed";
  onChange: (next: PairMode) => Promise<void> | void;
  size?: "sm" | "default";
  className?: string;
}

const OPTIONS: ReadonlyArray<{
  value: PairMode;
  label: string;
  hint: string;
}> = [
  { value: "manual", label: "Manual", hint: "Haiku silent. Operator drives." },
  { value: "semi_auto", label: "Semi", hint: "Haiku suggests, you approve." },
  { value: "full_auto", label: "Auto", hint: "Haiku trades autonomously." },
];

const LABEL: Record<PairMode, string> = {
  manual: "Manual",
  semi_auto: "Semi",
  full_auto: "Auto",
};

export function PairModeToggle({
  mode,
  status,
  onChange,
  size = "default",
  className,
}: PairModeToggleProps): ReactNode {
  const [optimistic, setOptimistic] = useState<PairMode | null>(null);
  const [busy, setBusy] = useState(false);
  const effective = optimistic ?? mode;
  const disabled = status !== "active" || busy;

  const handle = async (next: PairMode): Promise<void> => {
    if (disabled || next === effective) return;
    setOptimistic(next);
    setBusy(true);
    try {
      await onChange(next);
    } catch (e) {
      setOptimistic(null);
      console.error("[PairModeToggle] mode change failed", e);
    } finally {
      setBusy(false);
      queueMicrotask(() => setOptimistic(null));
    }
  };

  return (
    <Select
      value={effective}
      onValueChange={(v) => void handle(v as PairMode)}
      disabled={disabled}
    >
      <SelectTrigger
        size={size === "sm" ? "sm" : "default"}
        aria-label="AI mode for this pair"
        className={cn(
          "gap-2 text-accent",
          size === "sm" ? "text-[11px]" : "text-xs",
          "border-accent/40 hover:border-accent/60",
          disabled && "opacity-60",
          className,
        )}
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          AI Mode
        </span>
        <SelectValue>
          <span className="font-medium text-accent">{LABEL[effective]}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent
        align="end"
        alignItemWithTrigger={false}
        className="min-w-72"
      >
        {OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">
                {opt.label}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {opt.hint}
              </span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
